<template>
  <Teleport to="body">
    <Transition name="page-speed-bump-fade">
      <div
        v-if="showPageSpeedBump"
        class="page-speed-bump-backdrop"
        aria-hidden="true"></div>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <Transition name="page-speed-bump-fade">
      <div
        v-if="showPageSpeedBump"
        class="page-speed-bump"
        role="dialog"
        aria-modal="true"
        aria-labelledby="page-speed-bump-title">
        <div
          ref="dialog"
          data-lenis-prevent
          tabindex="-1"
          class="page-speed-bump-content">
          <svg
            class="page-speed-bump-icon"
            viewBox="0 0 40 40"
            fill="none"
            aria-hidden="true">
            <circle
              cx="20"
              cy="20"
              r="18.5"
              stroke="currentColor"
              stroke-width="3" />
            <circle
              cx="20"
              cy="12.5"
              r="2"
              fill="currentColor" />
            <path
              d="M20 18v11"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round" />
          </svg>

          <h2
            v-if="options.title"
            id="page-speed-bump-title"
            v-html="options.title"></h2>

          <div
            v-if="options.text"
            class="page-speed-bump-text"
            v-html="options.text"></div>

          <div class="page-speed-bump-actions">
            <ButtonGlobal
              color="dark"
              direction="left"
              :text="options.returnLabel"
              :func="cancel" />

            <ButtonGlobal
              :text="options.continueLabel"
              :func="confirm" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { usePageSpeedBump } from '@/components/composables/usePageSpeedBump'
import useSmoothScroll from '@/components/composables/useSmoothScroll'
import ButtonGlobal from '@/components/utility/ButtonGlobal.vue'
import { useStore } from '@/stores/main'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const store = useStore()
const { locoScroll } = useSmoothScroll()
const { showPageSpeedBump, confirm, cancel } = usePageSpeedBump()
const dialog = ref(null)

const options = computed(() => {
  const acfOptions = store.acfOptions() || {}

  return {
    title: acfOptions.page_speed_bump_title || '',
    text: acfOptions.page_speed_bump_text || '',
    returnLabel: acfOptions.page_speed_bump_return_label || 'Return',
    continueLabel: acfOptions.page_speed_bump_continue_label || 'Continue',
  }
})

watch(showPageSpeedBump, (value) => {
  if (value) {
    locoScroll.value?.stop?.()
    nextTick(() => dialog.value?.focus())
    return
  }

  locoScroll.value?.start?.()
})

onBeforeUnmount(() => {
  locoScroll.value?.start?.()
})
</script>

<style lang="scss">
.page-speed-bump-backdrop {
  @apply fixed inset-0 z-[102] bg-black/40 backdrop-blur-sm;
}

.page-speed-bump {
  @apply fixed inset-0 z-[103] flex items-center justify-center p-5;

  .page-speed-bump-content {
    @apply relative flex w-full max-w-[1000px] flex-col items-center rounded-[20px] bg-white/[0.85] px-6 py-14 text-center text-humble outline-none backdrop-blur-md md:p-[150px];

    .page-speed-bump-icon {
      @apply mb-8 h-10 w-10 text-humble;
    }

    h2 {
      @apply m-0 mb-[28px] font-FKDisplay font-normal normal-case text-humble;
      // Figma: FK Display Regular 60 / 60, -2% letter spacing, centered
      // (scaled down on small screens so the title doesn't overflow)
      @apply text-[36px] leading-[36px] tracking-[-0.02em] md:text-[60px] md:leading-[60px];
    }

    .page-speed-bump-text {
      @apply mx-auto mb-14 w-full max-w-[700px];

      * {
        @apply body m-0 font-FKGrotesk font-normal text-humble;
        // Figma: FK Grotesk Neue Regular 18 / 22, -1% letter spacing, centered
        font-size: 18px;
        line-height: 22px;
        letter-spacing: -0.01em;
      }

      p + p {
        @apply mt-4;
      }

      a {
        @apply underline;
      }
    }

    .page-speed-bump-actions {
      @apply flex flex-col items-center justify-center gap-4 lg:flex-row lg:gap-x-6;
    }
  }
}

.page-speed-bump-fade-enter-active,
.page-speed-bump-fade-leave-active {
  @apply transition-opacity duration-300;
}

.page-speed-bump-fade-enter-from,
.page-speed-bump-fade-leave-to {
  @apply opacity-0;
}
</style>
